import React, { useEffect, useReducer, useRef } from 'react';
import AuthContext from './AuthContext';
import { authReducer } from './AuthReducer';
import AuthRepositoryImpl from './AuthRepositoryImpl';
import { AuthActionType } from './AuthAction';
import { useLazyApi } from '../../core/functional/UseLazyApi';
import { withDefault } from '../../core/functional/Either';
import { User } from './User';
import { AuthData, authDataFromExchange, ExchangePayload } from './AuthData';
import { SignupPayload } from '../../core/models/SignupPayload';
import { AuthState } from './AuthState';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { environment } from '../../environment/environment';
import ProjectRepositoryImpl from '../../repositories/ProjectRepository/ProjectRepositoryImpl';

const AuthProvider = (props: {
  children: React.ReactNode;
}) => {
  const storageState = sessionStorage.getItem('auth');

  const parsedStorageState: AuthState = {
    ...storageState ? JSON.parse(storageState) : {},
    isLoading: false,
    isFirstLoading: true,
    isOnboardingCompleted: false,
    isUserLoading: true,
    isUpdateLocalUserLoading: true,
    isLoggingOut: false
  };

  const [state, dispatch] = useReducer(authReducer, parsedStorageState);
  const { fetch: getProfile, data: profile } = useLazyApi<User>();
  const { fetch: signup, data: signupData } = useLazyApi<AuthData>();
  const { fetch: completeLogin, fulfilledTimestamp: completeLoginDone, error: completeLoginError } = useLazyApi<void>();
  const { fetch: logout } = useLazyApi<void>();
  const { fetch: updateLocalUser, fulfilledTimestamp: updateLocalUserDone } = useLazyApi<void>();
  const location = useLocation();
  const navigate = useNavigate();
  /** A handle survives exactly one exchange; this effect can run many times. */
  const exchangeStarted = useRef(false);

  useEffect(() => {
    sessionStorage.setItem('auth', JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    const { handle, authCode } = queryString.parse(location.search);

    /**
     * Take the credential out of the URL for good.
     *
     * `location.search = ''` only mutated the object useLocation handed back -
     * under react-router v6 that is a plain value, so the URL never actually
     * changed and the param survived every re-render.
     *
     * That matters much more for a handle than it did for an authCode. This
     * effect re-runs on every `state` change, and a handle can only be exchanged
     * ONCE: the second attempt fails, and the axios response interceptor turns
     * that into a red error toast. So the param has to go, and `exchangeStarted`
     * guards the window before the navigate lands.
     */
    const clearAuthParams = () => {
      const remaining = queryString.parse(location.search);
      delete remaining.handle;
      delete remaining.authCode;
      const search = queryString.stringify(remaining);
      navigate({ pathname: location.pathname, search }, { replace: true });
    };

    const acceptAuthData = (authData: AuthData) => {
      clearAuthParams();
      dispatch({ type: AuthActionType.SET_AUTH_DATA, payload: { user: authData } });
      dispatch({ type: AuthActionType.SET_CREATED_SOFT_QUOTE, payload: { hasCreatedSoftQuote: true } });
    };

    if (handle && !Array.isArray(handle)) {
      /**
       * The handle is opaque and single-use, so it is exchanged for the payload
       * rather than decoded. Nothing sensitive rides in the URL any more.
       *
       * This branch owns the whole run while a handle is in the URL, started or
       * not. Guarding only the START of the exchange was not enough: the effect
       * re-runs on every state change, and updateLocalUser changes state within
       * milliseconds, so a second run would find exchangeStarted already true,
       * skip this branch and fall through to the signed-out `else` below - while
       * the exchange was still in flight. That either flashed the logged-out
       * screen or, with a session in sessionStorage, ran completeLogin on the old
       * token and wiped what the exchange was about to write.
       */
      if (exchangeStarted.current) {
        return;
      }

      exchangeStarted.current = true;

      AuthRepositoryImpl.exchangeHandle(handle).then(result => {
        const payload = withDefault<Optional<ExchangePayload>>(result, undefined);
        const authData = payload ? authDataFromExchange(payload) : undefined;

        if (authData) {
          acceptAuthData(authData);
          return;
        }

        /**
         * A failed exchange is an ordinary outcome, not an anomaly: the handle is
         * spent, expired after its minute, or the user reloaded or came back. It
         * used to leave the loading flags true for ever - isFirstLoading is built
         * from them - so the page just span. Drop the dead param and fall through
         * to the signed-out path on the next run.
         */
        clearAuthParams();
        logout(() => AuthRepositoryImpl.logout());
        dispatch({ type: AuthActionType.SET_USER_LOADING, payload: { loading: false } });
        dispatch({ type: AuthActionType.SET_UPDATE_LOCAL_USER_LOADING, payload: { loading: false } });
      });
    } else if (authCode && !Array.isArray(authCode)) {
      /**
       * Legacy base64 blob. Kept only so that a not-yet-redeployed producer app
       * keeps working through the rollout; remove once every producer emits a
       * handle.
       */
      const authData: Optional<AuthData> = JSON.parse(window.atob(authCode));
      if (authData) {
        acceptAuthData(authData);
      }
    } else {
      const token = state.authData?.token;

      if (token) {
        completeLogin(() => AuthRepositoryImpl.completeLogin(token));
      } else {
        logout(() => AuthRepositoryImpl.logout());

        if (state.user) {
          dispatch({ type: AuthActionType.DELETE_USER });
        }

        if (state.isUpdateLocalUserLoading) {
          dispatch({ type: AuthActionType.SET_UPDATE_LOCAL_USER_LOADING, payload: { loading: false } });
        }

        if (state.isUserLoading) {
          dispatch({ type: AuthActionType.SET_USER_LOADING, payload: { loading: false } });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    updateLocalUser(() => AuthRepositoryImpl.updateLocalUser());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeLoginDone]);

  useEffect(() => {
    if (updateLocalUserDone && state.isUpdateLocalUserLoading) {
      dispatch({ type: AuthActionType.SET_UPDATE_LOCAL_USER_LOADING, payload: { loading: false } });
    }

    if (updateLocalUserDone && state.isLoading) {
      dispatch({ type: AuthActionType.SET_LOADING, payload: { loading: false } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLocalUserDone]);

  useEffect(() => {
    if (completeLoginError) {
      if(process.env.REACT_APP_MODE !== 'production') console.log(completeLoginError);
      dispatch({ type: AuthActionType.DELETE_USER });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeLoginError]);

  useEffect(() => {
    const userData = state.authData;
    if (userData) {
      getProfile(() => AuthRepositoryImpl.getUser(userData.oldResponse.token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.authData]);

  useEffect(() => {
    if (signupData) {
      dispatch({ type: AuthActionType.SET_AUTH_DATA, payload: { user: signupData } });
    }
  }, [signupData]);

  useEffect(() => {
    if (profile) {
      dispatch({ type: AuthActionType.SET_PROFILE, payload: { profile } });
      dispatch({ type: AuthActionType.SET_USER_LOADING, payload: { loading: false } });
    }
  }, [profile]);

  const provided: React.ContextType<typeof AuthContext> = {
    state,
    dispatch,
    signup: async (data: SignupPayload) => {
      dispatch({ type: AuthActionType.SET_LOADING, payload: { loading: true } });
      return signup(() => AuthRepositoryImpl.signup(data));
    },
    signout: () => {
      dispatch({ type: AuthActionType.SET_LOGGING_OUT, payload: { loggingOut: true } });
      dispatch({ type: AuthActionType.DELETE_USER });
      window.open(`${environment.AUTH_APP_URL}?logout=true`, '_self');
    }
  };

  return (
    <AuthContext.Provider value={provided}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

